import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabase';

const ETAPAS = [
  { id: 'Cotización', color: '#F59E0B' },
  { id: 'Cita', color: '#3B82F6' },
  { id: 'Seguimiento', color: '#8B5CF6' },
  { id: 'Expediente', color: '#EC4899' },
  { id: 'Cierre ganado', color: '#10B981' },
  { id: 'Cierre perdido', color: '#EF4444' },
];

// FIX: control de tiempos entre etapas — evita "quemar" el proceso
// saltando de Cotización a Expediente en minutos. Solo se exige el
// mínimo al AVANZAR un paso dentro de esta secuencia; retroceder o
// mover a Cierre ganado/perdido queda libre. Super Admin/Admin/Sub
// Admin (ROLES_VER_TODOS) quedan exentos.
const ORDEN_ETAPAS = ['Cotización', 'Cita', 'Seguimiento', 'Expediente'];
const HORAS_MINIMAS_DEFAULT = { 'Cotización': 12, 'Cita': 12, 'Seguimiento': 24 };
const COLUMNA_ENTRADA = { 'Cotización': 'cotizacion_en', 'Cita': 'cita_en', 'Seguimiento': 'seguimiento_en', 'Expediente': 'expediente_en' };

// FIX: candado de orden — todos los roles menos Super Admin solo pueden
// avanzar a la siguiente etapa de esta secuencia, marcar Cierre perdido
// desde cualquier etapa abierta, o regresar a Cotización desde cualquier
// etapa para reiniciar el proceso. No se puede saltar etapas hacia adelante
// ni regresar a una etapa intermedia.
const SECUENCIA_ETAPAS = ['Cotización', 'Cita', 'Seguimiento', 'Expediente', 'Cierre ganado'];
const movimientoPermitido = (desde, hasta) => {
  if (desde === hasta) return false;
  if (hasta === 'Cotización') return true;
  if (hasta === 'Cierre perdido') return ['Cotización', 'Cita', 'Seguimiento', 'Expediente'].includes(desde);
  const i = SECUENCIA_ETAPAS.indexOf(desde);
  return i !== -1 && SECUENCIA_ETAPAS[i + 1] === hasta;
};
const MSJ_ORDEN = 'Solo se puede avanzar a la siguiente etapa (Cotización → Cita → Seguimiento → Expediente → Cierre ganado), marcar el negocio como Cierre perdido o regresarlo a Cotización para reiniciar el proceso. Para otro movimiento, pídelo a un Super Admin.';

const TIEMPO_COMPRA_OPCIONES = ['Inmediato', '1 a 3 meses', '3 a 6 meses', '6 a 12 meses', 'Más de 1 año'];
const fmt = (n) => `$${Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;
const ROLES_VER_TODOS = ['Super Admin', 'Admin', 'Sub Admin'];
const ROLES_GERENTE = ['Gerente Editor', 'Gerente Operador'];
// FIX: Gerente Externo también debe limitarse a sus propios desarrollos
// asignados para el selector/dropdown — pero a diferencia de Editor/
// Operador, además se restringe a "su gente" (ver cargarNegocios).
const ROLES_GERENTE_TODOS = [...ROLES_GERENTE, 'Gerente Externo'];
const ROLES_ASIGNAR = ['Super Admin'];

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

export default function Negocios() {
  const isMobile = useIsMobile();
  const [negocios, setNegocios] = useState([]);
  const [contactos, setContactos] = useState([]);
  const [desarrollos, setDesarrollos] = useState([]);
  const [agentes, setAgentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [miAgente, setMiAgente] = useState(null);
  const [miRol, setMiRol] = useState(null);
  // Desarrollador con permiso (agentes.ver_negocios): ve los negocios de su desarrollo, solo lectura.
  const soloLectura = miRol === 'Desarrollador';
  const [buscar, setBuscar] = useState('');
  const [filtroDesarrollo, setFiltroDesarrollo] = useState('');
  const [filtroAsesor, setFiltroAsesor] = useState('');
  const [filtroEtapa, setFiltroEtapa] = useState('');
  const [negocioDetalle, setNegocioDetalle] = useState(null);
  const [form, setForm] = useState(formVacio());
  // FIX: unidades del inventario del desarrollo elegido en el formulario,
  // para poder capturar con qué unidad arrancó el negocio.
  const [unidadesForm, setUnidadesForm] = useState([]);
  const [dragOver, setDragOver] = useState(null);
  const dragItem = useRef(null);
  // FIX: vigencia de negocios — mismo mecanismo que ya usan los
  // contactos (contactos.js): si un negocio no tiene ningún movimiento
  // de etapa en los días de vigencia configurados, se libera.
  const [vigenciaDias, setVigenciaDias] = useState(null);
  // FIX: fecha desde la que aplica el control de tiempos entre etapas —
  // se usa para marcar con un ícono de advertencia los negocios que se
  // saltaron una etapa o no cumplieron el mínimo (mismo criterio que ya
  // valida Movimientos.js al registrar el Apartado).
  const [cutoffTiempos, setCutoffTiempos] = useState(null);
  const [horasMinimas, setHorasMinimas] = useState(HORAS_MINIMAS_DEFAULT);
  // FIX: tareas del negocio abierto en el panel de detalle — tabla propia
  // (negocio_tareas), independiente de contacto_tareas.
  const [tareasNegocio, setTareasNegocio] = useState([]);
  const [nuevaTareaTexto, setNuevaTareaTexto] = useState('');
  const [fechaTareaNegocio, setFechaTareaNegocio] = useState('');
  const [horaTareaNegocio, setHoraTareaNegocio] = useState('');

  function formVacio() {
    return {
      nombre: '', contacto_id: '', valor: '', etapa: 'Cotización',
      fecha_cierre: '', asesor_ventas: '', asesor_correo: '',
      desarrollo: '', unidad: '', unidad_id: '', tiempo_compra: '', descripcion: ''
    };
  }

  useEffect(() => {
    cargarMiAgente();
    cargarAgentes();
    cargarVigencia();
    cargarCutoffTiempos();
    cargarHorasMinimas();
  }, []);

  const cargarHorasMinimas = async () => {
    const { data } = await supabase.from('configuracion').select('valor').eq('clave', 'negocios_horas_minimas').limit(1);
    try {
      const guardado = data?.[0]?.valor ? JSON.parse(data[0].valor) : null;
      if (guardado) setHorasMinimas({ ...HORAS_MINIMAS_DEFAULT, ...guardado });
    } catch (e) { /* valor corrupto: se quedan los valores por defecto */ }
  };

  // Etiqueta de una unidad con su torre/etapa y tipología, porque el número
  // se repite entre torres (por ejemplo, dos departamentos 502).
  const etiquetaUnidad = (u) => `${u.numero}${u.estructura ? ` — ${u.estructura}` : ''}${u.tipologia ? ` · ${u.tipologia}` : ''}${u.estatus && u.estatus !== 'Libre' ? ` (${u.estatus})` : ''}`;
  const etiquetaUnidadNegocio = (n) => {
    if (!n.unidad) return '';
    const inv = n.inventario;
    return inv?.estructura ? `${n.unidad} (${inv.estructura}${inv.tipologia ? ` · ${inv.tipologia}` : ''})` : n.unidad;
  };

  // Unidad elegida en el formulario: la guardada por id, o (en negocios
  // antiguos) la única que coincide con el número; si el número está en
  // varias torres queda sin elegir hasta que se escoja una.
  const coincidenciasUnidad = form.unidad ? unidadesForm.filter(u => u.numero === form.unidad) : [];
  const unidadIdSel = form.unidad_id || (coincidenciasUnidad.length === 1 ? coincidenciasUnidad[0].id : '');

  const handleEditarHorasMinimas = async (etapaId) => {
    if (miRol !== 'Super Admin') return;
    const entrada = window.prompt(`Horas mínimas que un negocio debe estar en "${etapaId}" antes de pasar a la siguiente etapa:`, horasMinimas[etapaId]);
    if (entrada === null) return;
    const horas = Number(String(entrada).replace(',', '.'));
    if (!Number.isFinite(horas) || horas < 0 || horas > 720) {
      alert('Escribe un número de horas entre 0 y 720.');
      return;
    }
    const nuevo = { ...horasMinimas, [etapaId]: horas };
    const { error } = await supabase.from('configuracion').upsert({ clave: 'negocios_horas_minimas', valor: JSON.stringify(nuevo) }, { onConflict: 'clave' });
    if (error) { alert('No se pudo guardar el tiempo: ' + error.message); return; }
    setHorasMinimas(nuevo);
  };

  const cargarCutoffTiempos = async () => {
    const { data } = await supabase.from('configuracion').select('valor').eq('clave', 'negocios_control_tiempos_desde').limit(1);
    if (data?.[0]?.valor) setCutoffTiempos(data[0].valor);
  };

  // FIX: mismo criterio que la validación del Apartado en Movimientos.js —
  // por cada tramo (Cotización→Cita, Cita→Seguimiento, Seguimiento→
  // Expediente), si el negocio ya llegó a la etapa destino pero le falta
  // la marca de la etapa anterior (se la saltó) o no pasó el tiempo
  // mínimo, se considera que "se saltó el proceso". Los negocios de antes
  // del control de tiempos quedan exentos.
  const saltoProceso = (n) => !n.liberado_tiempos_por && incumpleProceso(n);

  const incumpleProceso = (n) => {
    if (!cutoffTiempos || new Date(n.created_at) < new Date(cutoffTiempos)) return false;
    const horasEntre = (a, b) => (new Date(b) - new Date(a)) / (1000 * 60 * 60);
    // La etapa actual también cuenta como "ya llegó": un negocio en Expediente
    // sin fechas de Cita/Seguimiento se saltó el proceso aunque no tenga marcas.
    const nivelActual = { 'Cotización': 0, 'Cita': 1, 'Seguimiento': 2, 'Expediente': 3, 'Cierre ganado': 4 }[n.etapa] ?? -1;
    const tramos = [
      ['cotizacion_en', 'cita_en', horasMinimas['Cotización'], 1],
      ['cita_en', 'seguimiento_en', horasMinimas['Cita'], 2],
      ['seguimiento_en', 'expediente_en', horasMinimas['Seguimiento'], 3],
    ];
    return tramos.some(([colA, colB, minHoras, nivelB]) => {
      if (!n[colB] && nivelActual < nivelB) return false; // todavía no llega a esa etapa
      if (colA === 'cotizacion_en' && n.inicio_directo_cita) return false; // empezó directo en Cita (Showroom)
      if (!n[colA]) return true; // se saltó la etapa anterior
      if (!n[colB]) return true; // llegó a la etapa pero sin registrar la fecha
      return horasEntre(n[colA], n[colB]) < minHoras;
    });
  };

  // FIX: cargarContactos dependía de miRol/miAgente pero se llamaba antes
  // de que cargarMiAgente terminara, así que siempre corría en su rama
  // "sin filtro" — cualquier rol veía el listado completo de contactos
  // al crear/editar un negocio. Ahora espera a que miRol esté listo.
  useEffect(() => {
    if (miRol !== null) cargarContactos();
  }, [miRol, miAgente]);

  const cargarVigencia = async () => {
    const { data } = await supabase.from('configuracion').select('valor').eq('clave', 'vigencia_dias').single();
    if (data) setVigenciaDias(parseInt(data.valor));
  };

  // FIX: igual que verificarExpiracion en contactos.js — se corre cada
  // vez que se cargan los negocios y marca vigencia_activa=false a los
  // que no han tenido movimiento de etapa en vigenciaDias días.
  const verificarVigenciaNegocios = async (lista) => {
    if (!lista?.length || !vigenciaDias || soloLectura) return;
    const ahora = new Date();
    const vencidos = lista.filter(n => {
      if (!n.ultima_actividad || !n.vigencia_activa) return false;
      const dias = (ahora - new Date(n.ultima_actividad)) / (1000 * 60 * 60 * 24);
      return dias >= vigenciaDias;
    });
    for (const n of vencidos) {
      await supabase.from('negocios').update({ vigencia_activa: false }).eq('id', n.id);
    }
    if (vencidos.length > 0) setNegocios(prev => prev.map(n => vencidos.some(v => v.id === n.id) ? { ...n, vigencia_activa: false } : n));
  };

  useEffect(() => {
    if (negocioDetalle) cargarTareasNegocio(negocioDetalle.id);
    else { setTareasNegocio([]); setNuevaTareaTexto(''); setFechaTareaNegocio(''); setHoraTareaNegocio(''); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [negocioDetalle?.id]);

  const cargarTareasNegocio = async (negocioId) => {
    const { data } = await supabase.from('negocio_tareas').select('*').eq('negocio_id', negocioId).order('created_at', { ascending: false });
    setTareasNegocio(data || []);
  };

  // FIX: mismo mecanismo que reiniciarVigencia en DetalleContacto.js,
  // pero para negocios — crear o completar una tarea es lo único que
  // reinicia la vigencia (mover de etapa ya no la toca).
  const reiniciarVigenciaNegocio = async (negocioId) => {
    const nuevaFecha = new Date().toISOString();
    await supabase.from('negocios').update({ ultima_actividad: nuevaFecha, vigencia_activa: true }).eq('id', negocioId);
    setNegocioDetalle(n => (n && n.id === negocioId) ? { ...n, ultima_actividad: nuevaFecha, vigencia_activa: true } : n);
    setNegocios(prev => prev.map(n => n.id === negocioId ? { ...n, ultima_actividad: nuevaFecha, vigencia_activa: true } : n));
  };

  const handleAgregarTareaNegocio = async () => {
    if (!nuevaTareaTexto.trim() || !negocioDetalle) return;
    const texto = nuevaTareaTexto.trim();
    const payload = { negocio_id: negocioDetalle.id, texto, completada: false };
    // FIX: mismo patrón que handleAgregarTarea en DetalleContacto.js —
    // fecha/hora opcionales; si se capturan, se guarda el correo del
    // agente y arrancan los avisos en false para que "revisar-tareas"
    // (ya extendido para cubrir negocio_tareas) sepa que faltan por avisar.
    if (fechaTareaNegocio && horaTareaNegocio) {
      payload.fecha_hora = new Date(`${fechaTareaNegocio}T${horaTareaNegocio}`).toISOString();
      payload.agente_correo = miAgente?.correo || '';
      payload.aviso_5min_enviado = false;
      payload.aviso_hora_enviado = false;
    }
    const { data, error } = await supabase.from('negocio_tareas').insert([payload]).select().single();
    if (!error) {
      setTareasNegocio(prev => [data, ...prev]);
      setNuevaTareaTexto(''); setFechaTareaNegocio(''); setHoraTareaNegocio('');
      await reiniciarVigenciaNegocio(negocioDetalle.id);
    }
  };

  const handleToggleTareaNegocio = async (t) => {
    const nuevoEstado = !t.completada;
    await supabase.from('negocio_tareas').update({ completada: nuevoEstado }).eq('id', t.id);
    setTareasNegocio(prev => prev.map(x => x.id === t.id ? { ...x, completada: nuevoEstado } : x));
    await reiniciarVigenciaNegocio(t.negocio_id);
  };

  // FIX: cargarDesarrollos ahora depende de miAgente/miRol para poder
  // limitar el dropdown a los desarrollos a cargo de un Gerente.
  useEffect(() => {
    if (miRol !== null) cargarDesarrollos();
  }, [miRol, miAgente]);

  useEffect(() => {
    if (miRol !== null) cargarNegocios();
  }, [miRol, miAgente, buscar, filtroDesarrollo, filtroAsesor]);

  const cargarMiAgente = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data } = await supabase.from('agentes').select('*').eq('correo', user.email).single();
      setMiAgente(data || null);
      setMiRol(data?.rol || 'Agente');
    } else { setMiRol('Agente'); }
  };

  // FIX: el selector de Contacto (crear/editar negocio) mostraba TODOS los
  // contactos del sistema sin importar el rol. Mismo criterio que ya usan
  // Cotizador.js y contactos.js: Agente/Desarrollador solo los suyos
  // (asesor_ventas actual), Gerente Editor/Operador los que crearon o los
  // de sus desarrollos a cargo, Gerente Externo solo su gente dentro de
  // sus desarrollos, y Super Admin/Admin/Sub Admin sin restricción.
  const cargarContactos = async () => {
    let query = supabase.from('contactos').select('id, nombres, apellidos, fuente_medio').order('nombres');
    const correo = miAgente?.correo || '';
    const nombreCompleto = miAgente ? `${miAgente.nombre || ''} ${miAgente.apellidos || ''}`.trim() : '';
    const cargo = miAgente?.desarrollos_cargo || [];

    if (ROLES_VER_TODOS.includes(miRol)) {
      // sin filtro — ve todos
    } else if (miRol === 'Gerente Externo') {
      const agentesCargoCorreos = miAgente?.agentes_cargo || [];
      const { data: equipo } = agentesCargoCorreos.length > 0
        ? await supabase.from('agentes').select('nombre, apellidos').in('correo', agentesCargoCorreos)
        : { data: [] };
      const nombresEquipo = (equipo || []).map(a => `${a.nombre} ${a.apellidos}`.trim());
      const nombresFiltro = [nombreCompleto, ...nombresEquipo].filter(Boolean);
      if (cargo.length === 0) { setContactos([]); return; }
      query = nombresFiltro.length > 0
        ? query.or(`creado_por.eq.${correo},asesor_ventas.in.(${nombresFiltro.map(n => `"${n}"`).join(',')})`)
        : query.eq('creado_por', correo);
      query = query.in('desarrollo', cargo);
    } else if (ROLES_GERENTE.includes(miRol)) {
      query = cargo.length > 0
        ? query.or(`creado_por.eq.${correo},desarrollo.in.(${cargo.map(d => `"${d}"`).join(',')})`)
        : query.eq('creado_por', correo);
    } else {
      if (!nombreCompleto) { setContactos([]); return; }
      query = query.eq('asesor_ventas', nombreCompleto);
    }

    const { data } = await query;
    setContactos(data || []);
  };

  // FIX: el selector de desarrollo (para crear/editar un negocio, y el
  // filtro de arriba) ahora siempre muestra TODOS los desarrollos
  // activos, sin restringirse a desarrollos_cargo — antes, un Gerente sin
  // ningún desarrollo asignado a su cargo se quedaba con el dropdown
  // vacío y no podía capturar un negocio propio ni de un proyecto donde
  // apoya sin tener el cargo formal. La visibilidad real de qué negocios
  // puede VER cada rol la sigue controlando cargarNegocios, esto solo
  // afecta qué opciones aparecen en el desplegable.
  const cargarDesarrollos = async () => {
    const { data } = await supabase.from('desarrollos').select('id, nombre').eq('activo', true).order('nombre');
    setDesarrollos(soloLectura ? (data || []).filter(d => (miAgente?.desarrollos || []).includes(d.nombre)) : (data || []));
  };

  const cargarAgentes = async () => {
    const { data } = await supabase.from('agentes').select('id, nombre, apellidos, correo, rol').eq('activo', true).order('nombre');
    setAgentes(data || []);
  };

  // FIX: unidades disponibles para elegir en el formulario, según el
  // desarrollo seleccionado — mismo patrón que Movimientos.js.
  useEffect(() => {
    if (!showForm || !form.desarrollo) { setUnidadesForm([]); return; }
    const dev = desarrollos.find(d => d.nombre === form.desarrollo);
    if (!dev) { setUnidadesForm([]); return; }
    supabase.from('inventario').select('id, numero, estructura, tipologia, estatus').eq('desarrollo_id', dev.id).order('estructura').order('numero')
      .then(({ data }) => setUnidadesForm(data || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm, form.desarrollo, desarrollos]);

  // FIX: antes cualquier rol que no fuera Super Admin/Admin/Sub Admin
  // (incluidos los Gerentes) solo veía SUS PROPIOS negocios, como si
  // fueran Agente. Ahora los Gerentes ven todos los negocios de los
  // desarrollos que tienen a su cargo, no solo los suyos.
  // FIX BUG: además, un Gerente que trae un negocio propio (por ejemplo
  // sin desarrollo asignado, o de un desarrollo que no tiene a su cargo)
  // no lo veía, porque el filtro era SOLO "desarrollo IN cargo" y no
  // incluía "asesor_correo = él mismo". Ahora se combinan ambas
  // condiciones con OR, igual que ya se hace en Contactos.js.
  const cargarNegocios = async () => {
    setLoading(true);
    let query = supabase.from('negocios').select('*, contactos(nombres, apellidos, fuente_medio), inventario(estructura, tipologia)').order('created_at', { ascending: false });

    if (ROLES_VER_TODOS.includes(miRol)) {
      // ve todo, sin restricción
    } else if (miRol === 'Desarrollador') {
      // Solo con el permiso activado por el Super Admin, y solo los negocios de su desarrollo.
      const suyos = miAgente?.desarrollos || [];
      if (!miAgente?.ver_negocios || suyos.length === 0) { setNegocios([]); setLoading(false); return; }
      query = query.in('desarrollo', suyos);
    } else if (miRol === 'Gerente Externo') {
      // FIX: Gerente Externo (trabaja para el desarrollador) solo ve
      // negocios de SU GENTE (él + los agentes que tiene a cargo), y
      // solo dentro de sus desarrollos asignados — a diferencia de
      // Gerente Editor/Operador, que ven TODOS los negocios del
      // desarrollo sin importar el asesor. Aquí sí se puede cruzar por
      // correo de forma confiable porque `asesor_correo` ya existe.
      const cargo = miAgente?.desarrollos_cargo || [];
      const correo = miAgente?.correo || '';
      const equipoCorreos = [correo, ...(miAgente?.agentes_cargo || [])];
      if (cargo.length === 0 || !correo) { setNegocios([]); setLoading(false); return; }
      query = query.in('asesor_correo', equipoCorreos).in('desarrollo', cargo);
    } else if (ROLES_GERENTE.includes(miRol)) {
      const cargo = miAgente?.desarrollos_cargo || [];
      const correo = miAgente?.correo || '';
      if (cargo.length > 0 && correo) {
        const listaDesarrollos = cargo.map(d => `"${d.replace(/"/g, '\\"')}"`).join(',');
        query = query.or(`desarrollo.in.(${listaDesarrollos}),asesor_correo.eq.${correo}`);
      } else if (correo) {
        query = query.eq('asesor_correo', correo);
      } else {
        setNegocios([]); setLoading(false); return;
      }
    } else if (miAgente?.correo) {
      query = query.eq('asesor_correo', miAgente.correo);
    } else {
      setNegocios([]); setLoading(false); return;
    }

    if (buscar) query = query.ilike('nombre', `%${buscar}%`);
    if (filtroDesarrollo) query = query.eq('desarrollo', filtroDesarrollo);
    if (filtroAsesor) query = query.eq('asesor_ventas', filtroAsesor);
    const { data, error } = await query;
    if (!error) { setNegocios(data || []); verificarVigenciaNegocios(data || []); }
    setLoading(false);
  };

  const handleGuardar = async () => {
    if (!form.nombre.trim()) return;
    setGuardando(true);
    let asesorVentas = form.asesor_ventas;
    let asesorCorreo = form.asesor_correo;
    if (editando) {
      if (ROLES_ASIGNAR.includes(miRol)) {
        asesorVentas = form.asesor_ventas;
        asesorCorreo = form.asesor_correo;
      } else {
        const negocioOriginal = negocios.find(n => n.id === editando);
        asesorVentas = negocioOriginal?.asesor_ventas || form.asesor_ventas;
        asesorCorreo = negocioOriginal?.asesor_correo || form.asesor_correo;
      }
    } else {
      asesorVentas = miAgente ? `${miAgente.nombre} ${miAgente.apellidos}`.trim() : form.asesor_ventas;
      asesorCorreo = miAgente?.correo || '';
    }
    const payload = {
      nombre: form.nombre, contacto_id: form.contacto_id || null,
      valor: form.valor || 0, etapa: form.etapa,
      fecha_cierre: form.fecha_cierre || null, desarrollo: form.desarrollo || null,
      unidad: form.unidad || null, unidad_id: unidadIdSel || null,
      tiempo_compra: form.tiempo_compra || null, descripcion: form.descripcion || null,
      asesor_ventas: asesorVentas, asesor_correo: asesorCorreo,
      creado_por: form.creado_por || miAgente?.correo || '',
    };
    // FIX: sin esto, cotizacion_en se quedaba NULL para siempre en
    // negocios nuevos (nada más la volvía a tocar), lo que bloqueaba
    // permanentemente la validación de tiempos al registrar el Apartado.
    if (!editando) {
      const ahoraIso = new Date().toISOString();
      // Todo negocio nuevo empieza en Cotización, salvo los de contactos de
      // Showroom, que ya llegaron al punto de venta y empiezan en Cita.
      const esShowroom = contactos.find(c => c.id === form.contacto_id)?.fuente_medio === 'Showroom';
      payload.etapa = esShowroom ? 'Cita' : 'Cotización';
      payload.inicio_directo_cita = esShowroom;
      if (esShowroom) payload.cita_en = ahoraIso;
      else payload.cotizacion_en = ahoraIso;
      payload.ultima_actividad = ahoraIso;
      payload.vigencia_activa = true;
    }
    let error;
    if (editando) {
      ({ error } = await supabase.from('negocios').update(payload).eq('id', editando));
    } else {
      ({ error } = await supabase.from('negocios').insert([payload]));
    }
    setGuardando(false);
    if (!error) {
      setShowForm(false); setEditando(null); setNegocioDetalle(null); setForm(formVacio()); cargarNegocios();
    } else { alert('Error al guardar: ' + error.message); }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Eliminar este negocio?')) return;
    await supabase.from('negocios').delete().eq('id', id);
    setNegocioDetalle(null); cargarNegocios();
  };

  // FIX: recibe el negocio completo (no solo el id) para poder revisar
  // desde cuándo está en su etapa actual. Solo se exige el mínimo cuando
  // se avanza exactamente un paso dentro de ORDEN_ETAPAS; retroceder o
  // mandar a Cierre ganado/perdido no tiene mínimo. Se sella la fecha de
  // entrada a la etapa nueva y se reinicia la vigencia — mover de etapa
  // O hacer una tarea del negocio (ver handleAgregarTareaNegocio/
  // handleToggleTareaNegocio) cuentan igual como actividad reciente.
  const handleCambiarEtapa = async (negocio, nuevaEtapa) => {
    if (!negocio || negocio.etapa === nuevaEtapa || soloLectura) return;
    if (miRol !== 'Super Admin' && !movimientoPermitido(negocio.etapa, nuevaEtapa)) {
      alert(MSJ_ORDEN);
      return false;
    }
    const idxActual = ORDEN_ETAPAS.indexOf(negocio.etapa);
    const idxNueva = ORDEN_ETAPAS.indexOf(nuevaEtapa);
    const avanzaUnPaso = idxActual !== -1 && idxNueva === idxActual + 1;
    if (avanzaUnPaso && !ROLES_VER_TODOS.includes(miRol)) {
      const horasMinimasEtapa = horasMinimas[negocio.etapa];
      const colEntrada = COLUMNA_ENTRADA[negocio.etapa];
      const entradaEn = negocio[colEntrada] || negocio.created_at;
      if (horasMinimasEtapa && entradaEn) {
        const horasTranscurridas = (Date.now() - new Date(entradaEn).getTime()) / (1000 * 60 * 60);
        if (horasTranscurridas < horasMinimasEtapa) {
          const horasFaltan = Math.ceil(horasMinimasEtapa - horasTranscurridas);
          alert(`Todavía no se puede mover a "${nuevaEtapa}". Faltan ${horasFaltan} hora${horasFaltan === 1 ? '' : 's'} en "${negocio.etapa}".`);
          return false;
        }
      }
    }
    const colDestino = COLUMNA_ENTRADA[nuevaEtapa];
    const payload = { etapa: nuevaEtapa, ultima_actividad: new Date().toISOString(), vigencia_activa: true };
    if (colDestino) payload[colDestino] = new Date().toISOString();
    // Regresar a Cotización reinicia el proceso: se borran las marcas de las etapas posteriores.
    if (nuevaEtapa === 'Cotización') Object.assign(payload, { cita_en: null, seguimiento_en: null, expediente_en: null, inicio_directo_cita: false });
    await supabase.from('negocios').update(payload).eq('id', negocio.id);
    cargarNegocios();
    return true;
  };

  // FIX: el Super Admin autoriza un negocio en Expediente que no cumplió
  // los tiempos, para que el gerente registre el Apartado en Movimientos
  // (que reconoce liberado_tiempos_por) sin que el Super Admin tenga que
  // hacer la carga por él. Queda guardado quién y cuándo. Es una casilla,
  // así que se puede desmarcar para revertir la liberación.
  const handleToggleLiberarTiempos = async (negocio, liberar) => {
    if (miRol !== 'Super Admin') return;
    const cambios = liberar
      ? { liberado_tiempos_por: miAgente?.correo || 'Super Admin', liberado_tiempos_en: new Date().toISOString() }
      : { liberado_tiempos_por: null, liberado_tiempos_en: null };
    const { error } = await supabase.from('negocios').update(cambios).eq('id', negocio.id);
    if (error) { alert('No se pudo actualizar la liberación del negocio: ' + error.message); return; }
    setNegocioDetalle(n => (n && n.id === negocio.id) ? { ...n, ...cambios } : n);
    setNegocios(prev => prev.map(n => n.id === negocio.id ? { ...n, ...cambios } : n));
  };

  const handleDragStart = (negocio) => { dragItem.current = negocio; };
  const handleDragOver = (e, etapa) => { e.preventDefault(); setDragOver(etapa); };
  const handleDrop = async (e, etapa) => {
    e.preventDefault();
    if (dragItem.current && dragItem.current.etapa !== etapa) await handleCambiarEtapa(dragItem.current, etapa);
    setDragOver(null); dragItem.current = null;
  };
  const handleDragEnd = () => { setDragOver(null); dragItem.current = null; };

  const negociosFiltrados = negocios.filter(n =>
    (!buscar || n.nombre?.toLowerCase().includes(buscar.toLowerCase()) ||
      `${n.contactos?.nombres || ''} ${n.contactos?.apellidos || ''}`.toLowerCase().includes(buscar.toLowerCase())) &&
    (!filtroEtapa || n.etapa === filtroEtapa)
  );

  const negociosPorEtapa = (etapa) => negocios.filter(n => n.etapa === etapa && (
    !buscar || n.nombre?.toLowerCase().includes(buscar.toLowerCase()) ||
    `${n.contactos?.nombres || ''} ${n.contactos?.apellidos || ''}`.toLowerCase().includes(buscar.toLowerCase())
  ));

  const totalPorEtapa = (etapa) => negociosPorEtapa(etapa).reduce((sum, n) => sum + Number(n.valor || 0), 0);
  const asesoresUnicos = [...new Set(negocios.map(n => n.asesor_ventas).filter(Boolean))];
  const esSuperAdmin = ROLES_ASIGNAR.includes(miRol);
  // FIX: los Gerentes también ven varios negocios ahora, así que también
  // les sirve el filtro por asesor (antes solo Super Admin/Admin/Sub Admin)
  const puedeFiltrarPorAsesor = ROLES_VER_TODOS.includes(miRol) || ROLES_GERENTE_TODOS.includes(miRol) || soloLectura;

  return (
    <div style={{ padding: isMobile ? '1rem' : '2rem', height: isMobile ? 'auto' : '100vh', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexShrink: 0 }}>
        <div>
          <h2 style={{ fontSize: isMobile ? '17px' : '20px', fontWeight: '500', color: '#1a1a2e', marginBottom: '2px' }}>Negocios</h2>
          <div style={{ fontSize: '13px', color: '#888' }}>{negocios.length} negocios</div>
        </div>
        {!soloLectura && (
          <button onClick={() => {
            setForm({ ...formVacio(), asesor_ventas: miAgente ? `${miAgente.nombre} ${miAgente.apellidos}`.trim() : '', asesor_correo: miAgente?.correo || '' });
            setEditando(null); setShowForm(true);
          }} style={{ ...btnPrimary, padding: isMobile ? '10px 14px' : '8px 16px', fontSize: isMobile ? '14px' : '13px' }}>
            + {isMobile ? 'Nuevo' : 'Crear negocio'}
          </button>
        )}
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '1rem', flexShrink: 0, flexWrap: 'wrap' }}>
        <input placeholder='Buscar...' value={buscar} onChange={e => setBuscar(e.target.value)}
          style={{ padding: isMobile ? '10px 12px' : '7px 12px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px', flex: isMobile ? 1 : 'none', width: isMobile ? 'auto' : '160px' }} />
        {isMobile && (
          <select value={filtroEtapa} onChange={e => setFiltroEtapa(e.target.value)}
            style={{ ...filtroStyle, padding: '10px 12px', flex: 1 }}>
            <option value=''>Todas las etapas</option>
            {ETAPAS.map(e => <option key={e.id} value={e.id}>{e.id}</option>)}
          </select>
        )}
        {isMobile && (
          <select value={filtroDesarrollo} onChange={e => setFiltroDesarrollo(e.target.value)}
            style={{ ...filtroStyle, padding: '10px 12px', flex: '1 1 100%' }}>
            <option value=''>Todos los desarrollos</option>
            {desarrollos.map(d => <option key={d.id} value={d.nombre}>{d.nombre}</option>)}
          </select>
        )}
        {!isMobile && (
          <>
            <select value={filtroDesarrollo} onChange={e => setFiltroDesarrollo(e.target.value)} style={filtroStyle}>
              <option value=''>Desarrollo</option>
              {desarrollos.map(d => <option key={d.id} value={d.nombre}>{d.nombre}</option>)}
            </select>
            {puedeFiltrarPorAsesor && (
              <select value={filtroAsesor} onChange={e => setFiltroAsesor(e.target.value)} style={filtroStyle}>
                <option value=''>Asesor de ventas</option>
                {asesoresUnicos.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            )}
          </>
        )}
      </div>

      {/* Vista móvil: lista de tarjetas */}
      {isMobile ? (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>Cargando...</div>
          ) : negociosFiltrados.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#aaa', fontSize: '13px' }}>Sin negocios</div>
          ) : negociosFiltrados.map(n => {
            const etapa = ETAPAS.find(e => e.id === n.etapa);
            return (
              <div key={n.id} onClick={() => { setNegocioDetalle(n); setShowForm(false); }}
                style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '10px', padding: '14px 16px', marginBottom: '8px', cursor: 'pointer', borderLeft: `4px solid ${etapa?.color || '#ccc'}`, opacity: n.vigencia_activa === false ? 0.5 : 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                  <div style={{ fontWeight: '600', fontSize: '14px', color: '#1a1a2e', flex: 1, marginRight: '8px' }}>
                    {saltoProceso(n) && <span title='Se saltó una etapa o no cumplió el tiempo mínimo entre etapas' style={{ marginRight: '4px' }}>⚠️</span>}
                    {n.nombre}
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: etapa?.color, whiteSpace: 'nowrap' }}>{fmt(n.valor)}</div>
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
                  <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '20px', background: (etapa?.color || '#ccc') + '20', color: etapa?.color }}>{n.etapa}</span>
                  {n.desarrollo && <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '20px', background: '#f0f0f0', color: '#555' }}>{n.desarrollo}{n.unidad && ` · Unidad ${etiquetaUnidadNegocio(n)}`}</span>}
                  {n.vigencia_activa === false && <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '20px', background: '#FCEBEB', color: '#A32D2D' }}>Vencido</span>}
                </div>
                {n.contactos && <div style={{ fontSize: '12px', color: '#888' }}>👤 {n.contactos.nombres} {n.contactos.apellidos}</div>}
                {n.contactos?.fuente_medio && <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>📣 {n.contactos.fuente_medio}</div>}
                {n.asesor_ventas && <div style={{ fontSize: '12px', color: '#aaa', marginTop: '2px' }}>👔 {n.asesor_ventas}</div>}
              </div>
            );
          })}
        </div>
      ) : (
        /* Vista desktop: kanban */
        <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', flex: 1, paddingBottom: '1rem' }}>
          {ETAPAS.map(etapa => {
            const items = negociosPorEtapa(etapa.id);
            const total = totalPorEtapa(etapa.id);
            const isDragOver = dragOver === etapa.id;
            return (
              <div key={etapa.id}
                onDragOver={e => handleDragOver(e, etapa.id)}
                onDrop={e => handleDrop(e, etapa.id)}
                onDragLeave={() => setDragOver(null)}
                style={{ minWidth: '240px', width: '240px', display: 'flex', flexDirection: 'column', background: isDragOver ? '#f0f0f0' : '#f5f5f5', borderRadius: '12px', border: isDragOver ? `2px dashed ${etapa.color}` : '2px solid transparent', transition: 'all 0.15s' }}>
                <div style={{ padding: '12px 14px', borderBottom: '0.5px solid #e0e0e0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: etapa.color }} />
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a2e' }}>{etapa.id}</span>
                    </div>
                    <span style={{ fontSize: '12px', color: '#888', background: '#fff', padding: '2px 8px', borderRadius: '20px' }}>{items.length}</span>
                  </div>
                  <div style={{ fontSize: '11px', color: '#888' }}>{fmt(total)}</div>
                  {/* Altura fija en todas las columnas, tengan o no tiempo mínimo, para que las tarjetas queden alineadas. */}
                  <div style={{ marginTop: '6px', height: '24px', display: 'flex', alignItems: 'center' }}>
                    {horasMinimas[etapa.id] != null && (
                      miRol === 'Super Admin' ? (
                        <button onClick={() => handleEditarHorasMinimas(etapa.id)} title='Cambiar el tiempo mínimo en esta etapa'
                          style={{ background: '#fff', border: '0.5px solid #ddd', borderRadius: '20px', padding: '2px 10px', fontSize: '11px', color: '#555', cursor: 'pointer' }}>
                          ⏱ {horasMinimas[etapa.id]} h
                        </button>
                      ) : (
                        <div style={{ fontSize: '11px', color: '#aaa' }}>⏱ {horasMinimas[etapa.id]} h</div>
                      )
                    )}
                  </div>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
                  {loading ? (
                    <div style={{ fontSize: '12px', color: '#aaa', textAlign: 'center', padding: '1rem' }}>Cargando...</div>
                  ) : items.length === 0 ? (
                    <div style={{ fontSize: '12px', color: '#ccc', textAlign: 'center', padding: '1rem' }}>Sin negocios</div>
                  ) : items.map(n => (
                    <div key={n.id}
                      draggable={!soloLectura} onDragStart={() => handleDragStart(n)} onDragEnd={handleDragEnd}
                      onClick={() => { setNegocioDetalle(n); setShowForm(false); }}
                      style={{ background: '#fff', borderRadius: '8px', padding: '12px', marginBottom: '8px', cursor: soloLectura ? 'pointer' : 'grab', border: '0.5px solid #e0e0e0', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', userSelect: 'none', opacity: n.vigencia_activa === false ? 0.5 : 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a2e', marginBottom: '4px' }}>
                        {saltoProceso(n) && <span title='Se saltó una etapa o no cumplió el tiempo mínimo entre etapas' style={{ marginRight: '4px' }}>⚠️</span>}
                        {n.nombre}
                      </div>
                      {n.contactos && <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>👤 {n.contactos.nombres} {n.contactos.apellidos}</div>}
                      {n.desarrollo && <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>🏢 {n.desarrollo}{n.unidad && ` · Unidad ${etiquetaUnidadNegocio(n)}`}</div>}
                      {n.contactos?.fuente_medio && <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>📣 {n.contactos.fuente_medio}</div>}
                      <div style={{ fontSize: '12px', fontWeight: '600', color: etapa.color }}>{fmt(n.valor)}</div>
                      {n.fecha_cierre && <div style={{ fontSize: '11px', color: '#aaa', marginTop: '4px' }}>📅 {n.fecha_cierre}</div>}
                      {n.asesor_ventas && <div style={{ fontSize: '11px', color: '#aaa', marginTop: '2px' }}>👔 {n.asesor_ventas}</div>}
                      {n.vigencia_activa === false && <div style={{ fontSize: '11px', color: '#A32D2D', marginTop: '2px', fontWeight: '600' }}>⚠ Vencido</div>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Panel detalle */}
      {negocioDetalle && (
        <div style={{ position: 'fixed', top: 0, right: 0, width: isMobile ? '100%' : '400px', height: '100vh', background: '#fff', boxShadow: '-4px 0 20px rgba(0,0,0,0.1)', zIndex: 1000, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '1.25rem 1.5rem', borderBottom: '0.5px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
            <h3 style={{ fontSize: '16px', fontWeight: '500', margin: 0 }}>Detalle del negocio</h3>
            <button onClick={() => setNegocioDetalle(null)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#888' }}>×</button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.5rem' }}>
            <div style={{ fontSize: '18px', fontWeight: '600', color: '#1a1a2e', marginBottom: '4px' }}>{negocioDetalle.nombre}</div>
            <div style={{ display: 'flex', gap: '6px', marginBottom: '1.5rem' }}>
              <div style={{ display: 'inline-block', padding: '3px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '500',
                background: ETAPAS.find(e => e.id === negocioDetalle.etapa)?.color + '20',
                color: ETAPAS.find(e => e.id === negocioDetalle.etapa)?.color }}>
                {negocioDetalle.etapa}
              </div>
              {negocioDetalle.vigencia_activa === false && (
                <div style={{ display: 'inline-block', padding: '3px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '500', background: '#FCEBEB', color: '#A32D2D' }}>
                  Vencido — sin movimiento por {vigenciaDias}+ días
                </div>
              )}
            </div>
            {saltoProceso(negocioDetalle) && (
              <div style={{ padding: '10px 12px', marginBottom: '1.5rem', borderRadius: '8px', background: '#FFFBEA', border: '1px solid #FFE8A3' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '16px' }}>⚠️</span>
                  <span style={{ fontSize: '12px', color: '#7A5B00' }}>Este negocio se saltó una etapa o no cumplió el tiempo mínimo entre etapas. No se podrá registrar el Apartado hasta que un Super Admin lo autorice.</span>
                </div>
              </div>
            )}
            {negocioDetalle.liberado_tiempos_por && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', marginBottom: '1.5rem', borderRadius: '8px', background: '#EAF3DE', border: '1px solid #C5DDA8' }}>
                <span style={{ fontSize: '16px' }}>✅</span>
                <span style={{ fontSize: '12px', color: '#27500A' }}>
                  Liberado por {negocioDetalle.liberado_tiempos_por}{negocioDetalle.liberado_tiempos_en ? ` el ${new Date(negocioDetalle.liberado_tiempos_en).toLocaleDateString('es-MX')}` : ''}. Ya se puede registrar el Apartado.
                </span>
              </div>
            )}
            {miRol === 'Super Admin' && negocioDetalle.etapa === 'Expediente' && incumpleProceso(negocioDetalle) && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1.5rem', fontSize: '13px', color: '#333', cursor: 'pointer' }}>
                <input type='checkbox' checked={!!negocioDetalle.liberado_tiempos_por}
                  onChange={e => handleToggleLiberarTiempos(negocioDetalle, e.target.checked)}
                  style={{ width: '16px', height: '16px' }} />
                Liberado para registrar el Apartado (desmarca para revertir)
              </label>
            )}
            {[
              ['Contacto', negocioDetalle.contactos ? `${negocioDetalle.contactos.nombres} ${negocioDetalle.contactos.apellidos}` : '—'],
              ['Valor', fmt(negocioDetalle.valor)],
              ['Desarrollo', negocioDetalle.desarrollo || '—'],
              ['Unidad', etiquetaUnidadNegocio(negocioDetalle) || '—'],
              ['Fuente de medio', negocioDetalle.contactos?.fuente_medio || '—'],
              ['Asesor', negocioDetalle.asesor_ventas || '—'],
              ['Tiempo de compra', negocioDetalle.tiempo_compra || '—'],
              ['Fecha de cierre', negocioDetalle.fecha_cierre || '—'],
            ].map(([label, val]) => (
              <div key={label} style={{ display: 'flex', padding: '10px 0', borderBottom: '0.5px solid #f5f5f5', fontSize: '13px' }}>
                <span style={{ width: '140px', color: '#888', flexShrink: 0 }}>{label}</span>
                <span style={{ color: '#333', fontWeight: label === 'Valor' ? '600' : '400' }}>{val}</span>
              </div>
            ))}
            {negocioDetalle.descripcion && (
              <div style={{ marginTop: '1rem' }}>
                <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px' }}>DESCRIPCIÓN</div>
                <div style={{ fontSize: '13px', color: '#333', lineHeight: '1.6' }}>{negocioDetalle.descripcion}</div>
              </div>
            )}
            {!soloLectura && (<>
            <div style={{ marginTop: '1.5rem' }}>
              <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px' }}>MOVER A ETAPA</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {ETAPAS.filter(e => e.id !== negocioDetalle.etapa && (miRol === 'Super Admin' || movimientoPermitido(negocioDetalle.etapa, e.id))).map(e => (
                  <button key={e.id} onClick={async () => { const ok = await handleCambiarEtapa(negocioDetalle, e.id); if (ok) setNegocioDetalle({ ...negocioDetalle, etapa: e.id }); }}
                    style={{ padding: '6px 14px', borderRadius: '20px', border: `0.5px solid ${e.color}`, background: 'transparent', color: e.color, fontSize: '13px', cursor: 'pointer' }}>
                    {e.id}
                  </button>
                ))}
              </div>
            </div>
            {/* FIX: tareas propias del negocio (independientes de las de
                Contactos, misma UI que DetalleContacto.js) — crear o
                completar una, igual que mover de etapa, reinicia la
                vigencia del negocio. */}
            <div style={{ marginTop: '1.5rem' }}>
              <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px' }}>TAREAS</div>
              <div style={{ marginBottom: '12px' }}>
                <input value={nuevaTareaTexto} onChange={e => setNuevaTareaTexto(e.target.value)} placeholder='Nueva tarea...'
                  onKeyDown={e => e.key === 'Enter' && handleAgregarTareaNegocio()}
                  style={{ width: '100%', padding: '10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: isMobile ? '14px' : '13px', boxSizing: 'border-box', marginBottom: '8px' }} />
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input type='date' value={fechaTareaNegocio} onChange={e => setFechaTareaNegocio(e.target.value)}
                    style={{ flex: 1, padding: '10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: isMobile ? '14px' : '13px' }} />
                  <input type='time' value={horaTareaNegocio} onChange={e => setHoraTareaNegocio(e.target.value)}
                    style={{ flex: 1, padding: '10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: isMobile ? '14px' : '13px' }} />
                </div>
                <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '8px' }}>Fecha y hora son opcionales — si las pones, te llega un aviso 5 minutos antes y al llegar la hora.</div>
                <button onClick={handleAgregarTareaNegocio} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', padding: '12px' }}>Agregar tarea</button>
              </div>
              {tareasNegocio.length === 0 && <div style={{ fontSize: '13px', color: '#aaa', textAlign: 'center', padding: '1rem' }}>Sin tareas aún</div>}
              {tareasNegocio.map((t, i) => {
                const vencida = t.fecha_hora && !t.completada && new Date(t.fecha_hora) < new Date();
                return (
                  <div key={t.id || i} style={{ padding: '10px', background: '#f9f9f9', borderRadius: '8px', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      <input type='checkbox' checked={t.completada} onChange={() => handleToggleTareaNegocio(t)} style={{ marginTop: '3px', width: '18px', height: '18px', flexShrink: 0 }} />
                      <span style={{ fontSize: isMobile ? '14px' : '13px', color: '#333', textDecoration: t.completada ? 'line-through' : 'none', flex: 1 }}>{t.texto}</span>
                    </div>
                    <div style={{ fontSize: '11px', color: vencida ? '#A32D2D' : '#aaa', marginTop: '6px', paddingLeft: '28px' }}>
                      {t.fecha_hora ? (
                        <>{vencida ? '⚠️ Vencida — ' : '🔔 '}{new Date(t.fecha_hora).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</>
                      ) : new Date(t.created_at).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                );
              })}
            </div>
            </>)}
          </div>
          {!soloLectura && (
          <div style={{ padding: '1rem 1.5rem', borderTop: '0.5px solid #f0f0f0', flexShrink: 0, display: 'flex', gap: '8px' }}>
            <button onClick={() => {
              setForm({ ...formVacio(), ...negocioDetalle });
              setEditando(negocioDetalle.id);
              setNegocioDetalle(null);
              setShowForm(true);
            }} style={{ ...btnPrimary, flex: 1, justifyContent: 'center', padding: '12px' }}>Editar</button>
            <button onClick={() => handleEliminar(negocioDetalle.id)}
              style={{ padding: '12px 16px', background: '#FCEBEB', color: '#A32D2D', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
              Eliminar
            </button>
          </div>
          )}
        </div>
      )}

      {/* Panel crear/editar */}
      {showForm && (
        <div style={{ position: 'fixed', top: 0, right: 0, width: isMobile ? '100%' : '400px', height: '100vh', background: '#fff', boxShadow: '-4px 0 20px rgba(0,0,0,0.1)', zIndex: 1000, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '1.25rem 1.5rem', borderBottom: '0.5px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
            <h3 style={{ fontSize: '16px', fontWeight: '500', margin: 0 }}>{editando ? 'Editar negocio' : 'Crear negocio'}</h3>
            <button onClick={() => { setShowForm(false); setEditando(null); }} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#888' }}>×</button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.5rem' }}>
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Nombre del negocio <span style={{ color: '#e53e3e' }}>*</span></label>
              <input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} style={inputStyle} />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Contacto</label>
              <select value={form.contacto_id} onChange={e => setForm({ ...form, contacto_id: e.target.value })} style={inputStyle}>
                <option value=''>Selecciona...</option>
                {contactos.map(c => <option key={c.id} value={c.id}>{c.nombres} {c.apellidos}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Valor del negocio</label>
              <input type='number' value={form.valor} onChange={e => setForm({ ...form, valor: e.target.value })} style={inputStyle} placeholder='$0' />
            </div>
            {/* FIX: la etapa ya no se elige en este formulario — todo
                negocio nuevo nace en "Cotización", y para moverlo de
                etapa se usa "Mover a etapa" (respeta los tiempos
                mínimos entre etapas). Al editar, solo se muestra de
                referencia. */}
            {editando && (
              <div style={{ marginBottom: '12px' }}>
                <label style={labelStyle}>Etapa actual</label>
                <input readOnly value={editando ? form.etapa : (contactos.find(c => c.id === form.contacto_id)?.fuente_medio === 'Showroom' ? 'Cita' : 'Cotización')} style={{ ...inputStyle, background: '#f9f9f9', color: '#888', cursor: 'not-allowed' }} />
              </div>
            )}
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Fecha de cierre</label>
              <input type='date' value={form.fecha_cierre} onChange={e => setForm({ ...form, fecha_cierre: e.target.value })} style={inputStyle} />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>
                Asesor de ventas {esSuperAdmin && editando && <span style={{ color: '#3B82F6', fontWeight: '400' }}>(puedes reasignar)</span>}
              </label>
              {esSuperAdmin && editando ? (
                <select value={form.asesor_correo} onChange={e => {
                  const agente = agentes.find(a => a.correo === e.target.value);
                  setForm({ ...form, asesor_correo: e.target.value, asesor_ventas: agente ? `${agente.nombre} ${agente.apellidos}`.trim() : '' });
                }} style={inputStyle}>
                  <option value=''>Selecciona un asesor...</option>
                  {agentes.map(a => <option key={a.id} value={a.correo}>{a.nombre} {a.apellidos} ({a.rol})</option>)}
                </select>
              ) : (
                <input value={editando ? (form.asesor_ventas || '—') : (miAgente ? `${miAgente.nombre} ${miAgente.apellidos}`.trim() : '')}
                  readOnly style={{ ...inputStyle, background: '#f9f9f9', color: '#888', cursor: 'not-allowed' }} />
              )}
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Desarrollo</label>
              <select value={form.desarrollo} onChange={e => setForm({ ...form, desarrollo: e.target.value, unidad: '', unidad_id: '' })} style={inputStyle}>
                <option value=''>Selecciona...</option>
                {desarrollos.map(d => <option key={d.id} value={d.nombre}>{d.nombre}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Unidad</label>
              <select value={unidadIdSel || (form.unidad ? '__sin_torre__' : '')}
                onChange={e => {
                  const u = unidadesForm.find(x => x.id === e.target.value);
                  if (u) setForm({ ...form, unidad: u.numero, unidad_id: u.id });
                  else if (e.target.value === '') setForm({ ...form, unidad: '', unidad_id: '' });
                }}
                style={inputStyle} disabled={!form.desarrollo}>
                <option value=''>{form.desarrollo ? 'Selecciona...' : 'Elige primero un desarrollo'}</option>
                {form.unidad && !unidadIdSel && (
                  <option value='__sin_torre__'>{form.unidad} — elige la torre correcta</option>
                )}
                {unidadesForm.map(u => <option key={u.id} value={u.id}>{etiquetaUnidad(u)}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Tiempo de compra</label>
              <select value={form.tiempo_compra} onChange={e => setForm({ ...form, tiempo_compra: e.target.value })} style={inputStyle}>
                <option value=''>Elige una opción...</option>
                {TIEMPO_COMPRA_OPCIONES.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Descripción</label>
              <textarea value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} rows={3}
                style={{ ...inputStyle, resize: 'vertical' }} />
            </div>
          </div>
          <div style={{ padding: '1rem 1.5rem', borderTop: '0.5px solid #f0f0f0', flexShrink: 0 }}>
            <button onClick={handleGuardar} disabled={guardando}
              style={{ ...btnPrimary, width: '100%', padding: '14px', justifyContent: 'center', fontSize: '15px' }}>
              {guardando ? 'Guardando...' : editando ? 'Guardar cambios' : 'Crear negocio'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const btnPrimary = { background: '#4A4A4A', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', fontWeight: '500', display: 'flex', alignItems: 'center' };
const filtroStyle = { padding: '7px 12px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px', background: '#fff' };
const labelStyle = { fontSize: '11px', color: '#888', display: 'block', marginBottom: '4px' };
const inputStyle = { width: '100%', padding: '10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', background: '#fff' };